import {Box, Button, ActionIcon, Flex, Loader, Text, TextInput, Tooltip} from "@mantine/core";
import { useState, useEffect, useContext, useRef } from 'react';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import Time from '@/utils/time';
import ManageForm from "@/components/modals/form/manage-form";
import ManageFormModal from "@/PageComponents/Form/ManageFormModal";
import DownloadService from '@/utils/download-service';
import helper from "@/utils/helper";
import SCDropdownList from "@/components/sc-controls/form-controls/sc-dropdownlist";
import useRefState from "@/hooks/useRefState";
import SCModal from "@/PageComponents/Modal/SCModal";
import styles from './form-definitions-for-item.module.css';
import {
    IconCircleChevronUp,
    IconCopy,
    IconPencil,
    IconPrinter,
    IconSparkles
} from "@tabler/icons-react";
import {FormDefinition, FormHeader} from "@/interfaces/api/models";
import OptionService from '@/services/option/option-service';
import Constants from '@/utils/constants';
import ToastContext from '@/utils/toast-context';
import {
    buildDuplicateFormHeader,
    isDuplicateFormName,
    isFormHeaderEmpty,
    parseMaxDuplicateCount,
    suggestedDuplicateName,
} from './form-duplicate-helpers';
import { FORM_EVENTS, FormOpenTrigger, trackForm } from '@/PageComponents/Form/form-analytics';

interface Props {
    itemID: string;
    itemModule: number;
    customer?: any;
    supplier?: any;
    asset?: any;
    linkedFormDefinitions?: FormDefinition[];
    onClose: () => void;
    onlyLinkedForms?: boolean;
}

const FormDefinitionsForItem = ({ itemID, itemModule, customer, supplier, asset, linkedFormDefinitions, onClose, onlyLinkedForms = false }: Props) => {

    const [formHeaders, setFormHeaders] = useState<FormHeader[]>([]);
    const [formDefinitions, setFormDefinitions] = useState<FormDefinition[]>([]);
    const [manageFormHeader, setManageFormHeader] = useState<FormHeader | null>(null);
    const [manageFormHeaderV2, setManageFormHeaderV2] = useState<FormHeader | null>(null);
    const [selectedFormDefinition, setSelectedFormDefinition] = useState<FormDefinition | null>(null);
    const [isPrinting, setIsPrinting, getIsPrintingValue] = useRefState<FormHeader[]>([]);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [pendingEditorChoice, setPendingEditorChoice] = useState<FormHeader | null>(null);
    const [duplicateWarning, setDuplicateWarning] = useState<{ sourceHeader: FormHeader, masterID: string } | null>(null);
    const [duplicateNamePrompt, setDuplicateNamePrompt] = useState<FormHeader | null>(null);
    const [duplicateNameValue, setDuplicateNameValue] = useState('');
    const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null);
    // What opened the editor, sent with its analytics: held while the editor prompt is up, then
    // handed to whichever editor is chosen.
    const pendingEditorTriggerRef = useRef<FormOpenTrigger | null>(null);
    const [editorTrigger, setEditorTrigger] = useState<FormOpenTrigger | null>(null);

    const toast = useContext(ToastContext);

    const getFormHeaders = async () => {
        let formHeaderResponse = await Fetch.get({
            url: "/Form/GetByItemID",
            params: {
                itemID: itemID,
                itemModule: itemModule
            }
        });

        setFormHeaders(formHeaderResponse.Results);
    };

    const getFormDefinitions = async () => {
        let formDefinitionResponse = await Fetch.post({
            url: "/FormDefinition/GetFormDefinitions",
            params: {
                PageSize: 100,
                ShowDraftVersion: true
            }
        });

        let results: FormDefinition[] = formDefinitionResponse.Results;
        if (itemModule === Enums.Module.Customer) {
            results = results.filter((x: FormDefinition) => x.FormRule === Enums.FormRule.Customer);
        }

        setFormDefinitions(results);
    };

    const printFormHeader = async (formHeader: FormHeader) => {
        trackForm(FORM_EVENTS.printed, formHeader, null, { location: 'list' });
        let isPrintingTemp = [...getIsPrintingValue()];
        isPrintingTemp.push(formHeader);
        setIsPrinting(isPrintingTemp);
        DownloadService.downloadFile("GET", `/Form/PrintForm?formHeaderID=${formHeader.ID}`, null, true, false, "", "", null, false, () => {
            let isPrintingTemp = [...getIsPrintingValue()];
            isPrintingTemp = isPrintingTemp.filter((x: FormHeader) => x !== formHeader);
            setIsPrinting(isPrintingTemp);
        });
    };

    useEffect(() => {
        getFormDefinitions();
        getFormHeaders();
    }, []);

    const getAssociatedItemID = (module: number) => {
        switch (module) {
            case Enums.Module.Customer:
                return customer.ID;
            case Enums.Module.Asset:
                return asset.ID;
            case Enums.Module.Supplier:
                return supplier.ID;
            default:
                return null;
        }
    };

    const validForms = () => {
        let headersToShow: FormHeader[] = [...formHeaders];
        linkedFormDefinitions && linkedFormDefinitions.filter((x: FormDefinition) => x.IsActive).forEach((definition: FormDefinition) => {
            let idx = headersToShow.findIndex((x: FormHeader) => x.FormDefinition?.MasterID === definition.MasterID);
            if (idx < 0) {
                headersToShow.push({
                    FormDefinitionID: definition.ID,
                    FormDefinition: definition,
                    Module: itemModule,
                    ItemID: itemID,
                    FormStatus: Enums.FormStatus.Draft,
                    AssociatedModule: definition.Module,
                    AssociatedItemID: getAssociatedItemID(definition.Module!),
                    CompletedDate: null,
                    Invalid: false,
                    ID: helper.newGuid(),
                    _isNew: true
                });
            }
            else {
                headersToShow[idx]._isNew = false;
            }
        });

        return headersToShow.sort((a: FormHeader, b: FormHeader) => {
            return (a.FormDefinition?.Name || "") > (b.FormDefinition?.Name || "") ? 1 : -1;
        });
    };

    const unallocatedFormDefinitions = () => {
        let allocatedIDs = validForms().map((x: FormHeader) => x.FormDefinition?.MasterID);
        let unallocated = formDefinitions.filter((x: FormDefinition) => allocatedIDs.indexOf(x.MasterID) < 0);
        return unallocated.sort((a: FormDefinition, b: FormDefinition) => {
            return (a.Name || "") > (b.Name || "") ? 1 : -1;
        })
    }

    const getFormExpired = (formHeader: FormHeader) => {
        if (!formHeader.ExpireDate) {
            return false;
        }
        return Time.now().valueOf() > Time.parseDate(formHeader.ExpireDate).valueOf();
    };

    // A form that only exists locally (never saved) shouldn't be shown as "Draft" - that label
    // is reserved for forms that have actually been saved at least once but are incomplete.
    // Expiry supersedes the underlying FormStatus in the status column - a Completed form
    // whose ExpireDate has passed needs renewing, so it reads as "Expired" rather than "Completed".
    const renderStatusPill = (formHeader: FormHeader) => {
        if (formHeader._isNew) {
            return <span className={styles.statusNotStarted}>Not Started</span>;
        }
        if (getFormExpired(formHeader)) {
            return <span className={styles.expired}>Expired</span>;
        }
        const isCompleted = formHeader.FormStatus === Enums.FormStatus.Completed;
        return (
            <span className={`${styles.statusPill} ${isCompleted ? styles.statusCompleted : styles.statusDraft}`}>
                {Enums.getEnumStringValue(Enums.FormStatus, formHeader.FormStatus)}
            </span>
        );
    };

    const getFormHeader = async (formHeaderID: string): Promise<FormHeader> => {
        const result = await Fetch.get({
            url: "/Form",
            params: {
                id: formHeaderID
            }
        });
        return result;
    };

    const doManageFormHeader = async (formHeader: FormHeader) => {
        if (!formHeader._isNew) {
            const result = await getFormHeader(formHeader.ID!);

            setManageFormHeader({
                ...result,
                _isNew: false
            });
        } else {
            setManageFormHeader({
                ...formHeader,
                _isNew: true
            });
        }
    };

    const onSave = async (formHeader: FormHeader | null = null) => {
        setManageFormHeader(formHeader);
        setSelectedFormDefinition(null);
        getFormDefinitions();
        getFormHeaders();
    };

    const onRenew = async (formHeader: FormHeader) => {
        setManageFormHeader(null);
        // // need timeout to let hooks apply new state
        setTimeout(() => {
            editSelectedFormHeader(formHeader);
        }, 100);
    };

    const doManageFormHeaderV2 = async (formHeader: FormHeader) => {
        if (!formHeader._isNew) {
            const result = await getFormHeader(formHeader.ID!);
            setManageFormHeaderV2({ ...result, _isNew: false });
        } else {
            setManageFormHeaderV2({ ...formHeader, _isNew: true });
        }
    };

    const onSaveV2 = async (formHeader: FormHeader | null = null) => {
        setManageFormHeaderV2(formHeader);
        setSelectedFormDefinition(null);
        getFormDefinitions();
        getFormHeaders();
    };

    const onRenewV2 = async (formHeader: FormHeader) => {
        setManageFormHeaderV2(null);
        // // need timeout to let hooks apply new state
        setTimeout(() => {
            editSelectedFormHeaderV2(formHeader);
        }, 100);
    };

    const chooseEditorThenOpen = (formHeader: FormHeader, trigger: FormOpenTrigger) => {
        pendingEditorTriggerRef.current = trigger;
        setPendingEditorChoice(formHeader);
    };

    const resolveEditorChoice = (choice: 'v1' | 'v2') => {
        const formHeader = pendingEditorChoice;
        setPendingEditorChoice(null);
        if (!formHeader) {
            return;
        }
        setEditorTrigger(pendingEditorTriggerRef.current);
        if (choice === 'v1') {
            doManageFormHeader(formHeader);
        } else {
            doManageFormHeaderV2(formHeader);
        }
    };

    const buildRenewedFormHeader = async (formHeader: FormHeader): Promise<FormHeader> => {
        const result = await getFormHeader(formHeader.ID!);
        return {
            ...result,
            FormDefinition: formHeader.FormDefinition,
            Module: itemModule,
            ItemID: itemID,
            FormStatus: Enums.FormStatus.Draft,
            AssociatedModule: formHeader.FormDefinition?.Module,
            AssociatedItemID: getAssociatedItemID(formHeader.FormDefinition?.Module!),
            CompletedDate: null,
            Invalid: false,
            ExpireDate: null,
            _isNew: false
        };
    };

    const editSelectedFormHeader = async (formHeader: FormHeader) => {
        setEditorTrigger('renew');
        setManageFormHeader(await buildRenewedFormHeader(formHeader));
    };

    const editSelectedFormHeaderV2 = async (formHeader: FormHeader) => {
        setEditorTrigger('renew');
        setManageFormHeaderV2(await buildRenewedFormHeader(formHeader));
    };

    const addSelectedFormDefinition = (formDefinition: FormDefinition) => {
        const id = helper.newGuid();
        let formHeader: FormHeader = {
            ID: id,
            FormDefinitionID: formDefinition.ID,
            FormDefinition: formDefinition,
            Module: itemModule,
            ItemID: itemID,
            FormStatus: Enums.FormStatus.Draft,
            AssociatedModule: formDefinition.Module,
            AssociatedItemID: getAssociatedItemID(formDefinition.Module!),
            CompletedDate: null,
            Invalid: false,
            ExpireDate: null,
            FormDefinitionDisplayName: formDefinition.Name,
            FormDefinitionDescription: formDefinition.Description,
            _isNew: true
        };

        console.log(formHeader.ID, id);

        chooseEditorThenOpen(formHeader, 'add_other');
    }

    const groupedForms = () => {
        const forms = validForms();
        const groups: { masterID: string, headers: FormHeader[] }[] = [];
        const indexByMasterID = new Map<string, number>();

        forms.forEach((fh) => {
            const masterID = fh.FormDefinition?.MasterID ?? fh.FormDefinitionID;
            if (masterID && indexByMasterID.has(masterID)) {
                groups[indexByMasterID.get(masterID)!].headers.push(fh);
            } else if (masterID) {
                indexByMasterID.set(masterID, groups.length);
                groups.push({ masterID, headers: [fh] });
            }
        });

        groups.forEach(group => {
            if (group.headers.length > 1) {
                group.headers.sort((a, b) => {
                    const aDate = a.CreatedDate ? new Date(a.CreatedDate).getTime() : 0;
                    const bDate = b.CreatedDate ? new Date(b.CreatedDate).getTime() : 0;
                    return aDate - bDate;
                });
            }
        });

        return groups;
    };

    const toggleGroup = (masterID: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(masterID)) next.delete(masterID); else next.add(masterID);
            return next;
        });
    };

    const expandGroup = (masterID: string) => {
        setCollapsedGroups(prev => {
            if (!prev.has(masterID)) return prev;
            const next = new Set(prev);
            next.delete(masterID);
            return next;
        });
    };

    const groupFor = (formHeader: FormHeader) => {
        const masterID = formHeader.FormDefinition?.MasterID ?? formHeader.FormDefinitionID ?? '';
        return groupedForms().find(g => g.masterID === masterID);
    };

    // Mirrors mobile's duplicateFormHeader (module-forms.page.ts): enforce the configured
    // max-duplicate-count, warn if there are already 2+ unused copies, then prompt for a name.
    const duplicateFormHeader = async (sourceHeader: FormHeader) => {
        const masterID = sourceHeader.FormDefinition?.MasterID ?? sourceHeader.FormDefinitionID;
        if (!masterID) return;

        const groupHeaders = groupFor(sourceHeader)?.headers ?? [sourceHeader];

        const maxCountValue = await OptionService.getOptionValue(Constants.optionKeys.MaxFormDuplicateCount);
        const maxCount = parseMaxDuplicateCount(maxCountValue);

        if (groupHeaders.length >= maxCount) {
            trackForm(FORM_EVENTS.duplicateLimitReached, sourceHeader, null, { max_count: maxCount });
            toast.setToast!!!({ message: `You have reached your form limit (${maxCount}).`, show: true, type: 'error' });
            return;
        }

        const emptinessChecks = await Promise.all(groupHeaders.map(async (h) => {
            const full = await getFormHeader(h.ID!);
            return isFormHeaderEmpty(full?.FormItems);
        }));
        const emptyCount = emptinessChecks.filter(Boolean).length;

        if (emptyCount >= 2) {
            setDuplicateWarning({ sourceHeader, masterID });
            return;
        }

        openDuplicateNamePrompt(sourceHeader);
    };

    const openDuplicateNamePrompt = (sourceHeader: FormHeader) => {
        const baseName = sourceHeader.FormDefinitionDisplayName || sourceHeader.FormDefinition?.Name;
        setDuplicateNameValue(suggestedDuplicateName(baseName));
        setDuplicateNameError(null);
        setDuplicateNamePrompt(sourceHeader);
    };

    const confirmDuplicateName = () => {
        if (!duplicateNamePrompt) return;
        const sourceHeader = duplicateNamePrompt;
        const groupHeaders = groupFor(sourceHeader)?.headers ?? [sourceHeader];
        const existingNames = groupHeaders.map(h => h.FormDefinitionDisplayName || h.FormDefinition?.Name);

        if (isDuplicateFormName(duplicateNameValue, existingNames)) {
            setDuplicateNameError('This name is already in use');
            return;
        }

        const newID = helper.newGuid();
        const associatedItemID = getAssociatedItemID(sourceHeader.FormDefinition?.Module!);
        const newHeader = buildDuplicateFormHeader(sourceHeader, newID, duplicateNameValue.trim(), itemID, itemModule, associatedItemID);

        setDuplicateNamePrompt(null);
        chooseEditorThenOpen(newHeader, 'duplicate');
        toast.setToast!!!({ message: 'Form duplicated successfully', show: true, type: 'success' });
    };

    // Fixed 3-slot layout (edit / duplicate / print) so the action column never shifts
    // width depending on which actions are available for a given row.
    const renderRowActions = (formHeader: FormHeader) => {
        const notStarted = !!formHeader._isNew;
        const canDuplicate = !notStarted && !!formHeader.FormDefinition?.MultiUse;
        const canPrint = !notStarted;
        const isPrinting = getIsPrintingValue().find((x) => x === formHeader);
        const isCompleted = formHeader.FormStatus === Enums.FormStatus.Completed;
        const editLabel = notStarted
            ? "Start form"
            : isCompleted
                ? "View completed form"
                : "Edit draft form";
        const openTrigger: FormOpenTrigger = notStarted ? 'start' : isCompleted ? 'view_completed' : 'edit_draft';

        return (
            <td className={styles.fitColumn}>
                <div className={styles.formActions}>
                    <Tooltip label={editLabel}>
                        <ActionIcon
                            variant="subtle"
                            color="dark"
                            onClick={() => chooseEditorThenOpen(formHeader, openTrigger)}
                            aria-label={editLabel}
                        >
                            <IconPencil size={18} />
                        </ActionIcon>
                    </Tooltip>
                    {canDuplicate ? (
                        <Tooltip label="Duplicate">
                            <ActionIcon
                                variant="subtle"
                                color="dark"
                                onClick={() => duplicateFormHeader(formHeader)}
                                aria-label="Duplicate"
                            >
                                <IconCopy size={18} />
                            </ActionIcon>
                        </Tooltip>
                    ) : <span aria-hidden="true" />}
                    {canPrint ? (
                        isPrinting ? (
                            <Loader size={18} color="dark"/>
                        ) : (
                            <Tooltip label="Print">
                                <ActionIcon
                                    variant="subtle"
                                    color="dark"
                                    onClick={() => printFormHeader(formHeader)}
                                    aria-label="Print"
                                >
                                    <IconPrinter size={18} />
                                </ActionIcon>
                            </Tooltip>
                        )
                    ) : <span aria-hidden="true" />}
                </div>
            </td>
        );
    };

    const savedFormCount = validForms().filter((x) => !x._isNew).length;

    return (<>
    <SCModal open={!manageFormHeader && !manageFormHeaderV2 && !pendingEditorChoice && !duplicateWarning && !duplicateNamePrompt}
             size={1100}
    >
        <Box mt={'sm'} className="title">
            <h1>Allocated Forms</h1>
        </Box>
        {savedFormCount > 0 && (
            <div className={styles.formCount}>{savedFormCount} form{savedFormCount !== 1 ? 's' : ''}</div>
        )}

        <div className={styles.tableContainer}>
            <table className={styles.table}>
                <thead>
                <tr>
                    <th>
                        <Flex align={'center'}>
                            <ActionIcon
                                variant="transparent"
                                color="dark"
                                style={{ visibility: 'hidden' }}
                            >
                                <IconCircleChevronUp size={16} />
                            </ActionIcon>
                            <span>Name</span>
                        </Flex>
                    </th>
                    <th>For</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th className={styles.narrowColumn}></th>
                </tr>
                </thead>
                <tbody>
                {groupedForms().flatMap(({ masterID, headers }) => {
                    const isGroup = headers.length > 1;
                    const rows: JSX.Element[] = [];

                    if (headers.length === 1) {
                        const formHeader = headers[0];
                        rows.push(
                            <tr key={formHeader.ID}>
                                <td>
                                    <Flex align={'center'}>
                                        <ActionIcon
                                            color={'dark'}
                                            variant="transparent"
                                            style={{ visibility: 'hidden' }}
                                        >
                                            <IconCircleChevronUp size={16} />
                                        </ActionIcon>
                                        <Text size={'sm'}>
                                            {formHeader.FormDefinition?.Name}
                                        </Text>
                                    </Flex>
                                </td>
                                <td>{Enums.getEnumStringValue(Enums.FormRule, formHeader.FormDefinition?.FormRule)}</td>
                                <td>{formHeader.FormDefinition?.Version}</td>
                                <td>{renderStatusPill(formHeader)}</td>
                                {renderRowActions(formHeader)}
                            </tr>
                        );
                    } else {
                        const parent = headers[0];
                        const copies = headers.slice(1);
                        const isCollapsed = collapsedGroups.has(masterID);

                        if (isGroup) {
                            rows.push(<tr key={`${masterID}-spacer-top`} className={styles.groupSpacerRow}><td colSpan={5}></td></tr>);
                        }

                        rows.push(
                            <tr key={`${masterID}-parent`} className={styles.groupParentRow}>
                                <td>
                                    <Flex align={'center'}>
                                        <ActionIcon
                                            variant="transparent"
                                            color="dark"
                                            onClick={() => toggleGroup(masterID)}
                                            aria-label={isCollapsed ? 'Expand copies' : 'Collapse copies'}
                                        >
                                            <IconCircleChevronUp
                                                className={`${styles.toggleIcon} ${!isCollapsed ? styles.toggleIconExpanded : ''}`}
                                                size={16}
                                            />
                                        </ActionIcon>
                                        <Text size={'sm'}>
                                            {parent.FormDefinition?.Name}
                                        </Text>
                                    </Flex>
                                </td>
                                <td>{Enums.getEnumStringValue(Enums.FormRule, parent.FormDefinition?.FormRule)}</td>
                                <td>{parent.FormDefinition?.Version}</td>
                                <td>{renderStatusPill(parent)}</td>
                                {renderRowActions(parent)}
                            </tr>
                        );

                        if (!isCollapsed) {
                            rows.push(...copies.map((copy, index) => {
                                return (
                                    <tr key={copy.ID} className={`${styles.copyRow} ${index % 2 === 0 ? styles.copyRowEven : styles.copyRowOdd}`}>
                                        <td className={styles.copyName}>
                                            {copy.FormDefinitionDisplayName || copy.FormDefinition?.Name}
                                        </td>
                                        <td></td>
                                        <td>{copy.FormDefinition?.Version}</td>
                                        <td>{renderStatusPill(copy)}</td>
                                        {renderRowActions(copy)}
                                    </tr>
                                );
                            }));
                        }

                        if (isGroup) {
                            rows.push(<tr key={`${masterID}-spacer-bottom`} className={styles.groupSpacerRow}><td colSpan={5}></td></tr>);
                        }
                    }

                    return rows;
                })}
                </tbody>
            </table>
        </div>

        {!onlyLinkedForms && unallocatedFormDefinitions().length > 0 ? <>
            <div className={styles.row}>
                <div className={styles.column}>
                    <SCDropdownList
                        options={unallocatedFormDefinitions()}
                        label="Other forms"
                        onChange={setSelectedFormDefinition}
                        value={selectedFormDefinition}
                        dataItemKey="ID"
                        textField="Name"
                        canClear={true}
                    />
                </div>
                <div className={styles.column} style={{position: "relative"}}>
                    {selectedFormDefinition ? <div style={{position: "absolute", bottom: 0}}>
                        <Button onClick={() => addSelectedFormDefinition(selectedFormDefinition)}>Add Form</Button>
                    </div> : ""}
                </div>
            </div>
        </> : ""}

        <div>
            <div>
                <Button mt={'sm'} onClick={onClose} variant={'outline'}>
                    Close
                </Button>
            </div>
        </div>
    </SCModal>

    {pendingEditorChoice ? <>
        <SCModal open={!!pendingEditorChoice}
                 size={440}
                 onClose={() => setPendingEditorChoice(null)}
        >
            <Flex align={'center'} gap={'xs'} mb={'xs'}>
                <Text size={'lg'} fw={700} c={'scBlue'}>Beta form editor</Text>
                <span className={styles.betaTag}>Beta</span>
            </Flex>
            <Text size={'sm'} c={'dimmed'} mb={'lg'}>
                Use the beta form editor to fill this form in with a cleaner layout, section
                navigation and inline validation. It&apos;s still in beta &mdash; you can switch
                back to the original editor at any time.
            </Text>
            <Flex gap={'sm'} justify={'flex-end'}>
                <Button variant={'outline'} onClick={() => resolveEditorChoice('v1')}>
                    Use original
                </Button>
                <Button onClick={() => resolveEditorChoice('v2')}>
                    Use beta editor
                </Button>
            </Flex>
        </SCModal>
    </> : ""}

    {duplicateWarning ? <>
        <SCModal open={!!duplicateWarning}
                 size={440}
                 onClose={() => {
                     trackForm(FORM_EVENTS.duplicateWarningAnswered, duplicateWarning.sourceHeader, null, { choice: 'dismissed' });
                     setDuplicateWarning(null);
                 }}
        >
            <Text size={'lg'} fw={600} mb={'xs'}>Heads up! You have made some unused copies of this form.</Text>
            <Text size={'sm'} c={'dimmed'} mb={'lg'}>
                Because forms can't be deleted, it's best to complete or use them before creating more.
            </Text>
            <Flex gap={'sm'} justify={'flex-end'}>
                <Button variant={'outline'} onClick={() => {
                    const sourceHeader = duplicateWarning.sourceHeader;
                    trackForm(FORM_EVENTS.duplicateWarningAnswered, sourceHeader, null, { choice: 'reuse_anyway' });
                    setDuplicateWarning(null);
                    openDuplicateNamePrompt(sourceHeader);
                }}>
                    Re-use anyway
                </Button>
                <Button onClick={() => {
                    trackForm(FORM_EVENTS.duplicateWarningAnswered, duplicateWarning.sourceHeader, null, { choice: 'view_existing' });
                    expandGroup(duplicateWarning.masterID);
                    setDuplicateWarning(null);
                }}>
                    View existing copies
                </Button>
            </Flex>
        </SCModal>
    </> : ""}

    {duplicateNamePrompt ? <>
        <SCModal open={!!duplicateNamePrompt}
                 size={440}
                 onClose={() => setDuplicateNamePrompt(null)}
        >
            <Text size={'lg'} fw={600} mb={'md'}>Name the new form</Text>
            <TextInput
                label="Form name"
                value={duplicateNameValue}
                onChange={(e) => {
                    setDuplicateNameValue(e.currentTarget.value);
                    setDuplicateNameError(null);
                }}
                error={duplicateNameError}
                mb={'lg'}
            />
            <Flex gap={'sm'} justify={'flex-end'}>
                <Button variant={'outline'} onClick={() => setDuplicateNamePrompt(null)}>
                    Cancel
                </Button>
                <Button onClick={confirmDuplicateName}>
                    Create
                </Button>
            </Flex>
        </SCModal>
    </> : ""}

    {manageFormHeader ? <>
        <ManageForm
            onSave={onSave}
            onRenew={onRenew}
            formHeaderToManage={manageFormHeader}
            isNew={manageFormHeader._isNew}
            openTrigger={editorTrigger}
        />
    </> : ""}

    {manageFormHeaderV2 ? <>
        <ManageFormModal
            onSave={onSaveV2}
            onRenew={onRenewV2}
            formHeaderToManage={manageFormHeaderV2}
            isNew={!!manageFormHeaderV2._isNew}
            openTrigger={editorTrigger}
        />
    </> : ""}
    </>);
};

export default FormDefinitionsForItem;